
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BrandingData {
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  companyName?: string;
}

interface OrganizationBrandingProps {
  organizationId: string;
}

export function OrganizationBranding({ organizationId }: OrganizationBrandingProps) {
  const [branding, setBranding] = useState<BrandingData>({
    primaryColor: "#3b82f6",
    secondaryColor: "#64748b"
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBranding();
  }, [organizationId]);

  const fetchBranding = async () => {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("branding")
        .eq("id", organizationId)
        .single();

      if (error) throw error;
      
      if (data.branding && typeof data.branding === 'object') {
        setBranding(prev => ({ ...prev, ...(data.branding as BrandingData) }));
      }
    } catch (error) {
      console.error("Error fetching branding:", error);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const brandingData = {
        logo: branding.logo || null,
        primaryColor: branding.primaryColor || null,
        secondaryColor: branding.secondaryColor || null,
        companyName: branding.companyName || null,
      };

      const { error } = await supabase
        .from("organizations")
        .update({ branding: brandingData })
        .eq("id", organizationId);

      if (error) throw error;

      toast({
        title: "Branding Updated",
        description: "Your organization branding has been saved successfully!",
      });
    } catch (error: any) {
      console.error("Error updating branding:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update branding",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Branding</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            value={branding.companyName || ""}
            onChange={(e) => setBranding(prev => ({ ...prev, companyName: e.target.value }))}
            placeholder="Your Company Name"
          />
        </div>

        <div>
          <Label htmlFor="logo">Logo URL</Label>
          <Input
            id="logo"
            value={branding.logo || ""}
            onChange={(e) => setBranding(prev => ({ ...prev, logo: e.target.value }))}
            placeholder="https://example.com/logo.png"
          />
          {branding.logo && (
            <div className="mt-2">
              <img
                src={branding.logo}
                alt="Logo preview"
                className="h-12 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="primaryColor">Primary Color</Label>
          <div className="flex items-center space-x-2">
            <Input
              id="primaryColor"
              type="color"
              value={branding.primaryColor}
              onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
              className="w-20 h-10"
            />
            <Input
              value={branding.primaryColor}
              onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
              placeholder="#3b82f6"
              className="flex-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="secondaryColor">Secondary Color</Label>
          <div className="flex items-center space-x-2">
            <Input
              id="secondaryColor"
              type="color"
              value={branding.secondaryColor}
              onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
              className="w-20 h-10"
            />
            <Input
              value={branding.secondaryColor}
              onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
              placeholder="#64748b"
              className="flex-1"
            />
          </div>
        </div>

        <div className="pt-4">
          <h4 className="font-medium mb-2">Preview</h4>
          <div 
            className="p-4 rounded-lg border"
            style={{ 
              backgroundColor: branding.primaryColor + '10',
              borderColor: branding.primaryColor
            }}
          >
            <div className="flex items-center space-x-3">
              {branding.logo && (
                <img src={branding.logo} alt="Logo" className="h-8 w-auto" />
              )}
              <span 
                className="font-semibold"
                style={{ color: branding.primaryColor }}
              >
                {branding.companyName || "Your Company"}
              </span>
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={isLoading} className="w-full">
          {isLoading ? "Saving..." : "Save Branding"}
        </Button>
      </CardContent>
    </Card>
  );
}
